import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
} from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import moment from 'moment';
import * as Sentry from '@sentry/browser';
import {
  Button,
  Chip,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  OutlinedInput,
} from '@mui/material';

import LoggedInUserContext from '../../../context/LoggedInUserContext';
import CoachTaskAdminContext from '../../context/CoachTaskAdminContext';
import ContentHighlight from '../../Model/ContentHighlight';
import { DateFormat } from '../../../utils/date';
import GenericDataGrid from '../../components/GenericDataGrid/GenericDataGrid';
import BackButton from '../../components/BackButton/BackButton';
import CreateCoachTaskModal from '../../layout/components/CreateCoachTaskModal';
import useToast from '../../hooks/useToast';
import ConfirmationModal from '../../../components/v2/ConfirmationModal/ConfirmationModal';
import {
  HeaderRow,
  TitleContainer,
  Title,
  SubTitle,
} from '../../../components/v2/Header';

import {
  PageContainer,
  GridWrapper,
  HeaderActions,
  ActionButtons,
  OverdueText,
} from './styles';
import texts from './texts.json';
import { TaskDisplayStatus, displayStatusOf } from './utils';

// Statuses shown on the detail page (REMOVED is excluded upstream, bar a client's no to a highlight).
const STATUS_OPTIONS = [
  TaskDisplayStatus.IN_PROGRESS,
  TaskDisplayStatus.AWAITING_CLIENT_CONSENT,
  TaskDisplayStatus.CLIENT_DECLINED,
  TaskDisplayStatus.COMPLETED,
];

/*
  The default hides what is finished. A highlight still waiting on the client stays in view: it is open work
  the AC just sent, and the only place anyone can see it is waiting.
*/
const DEFAULT_STATUS_FILTER = [TaskDisplayStatus.IN_PROGRESS, TaskDisplayStatus.AWAITING_CLIENT_CONSENT];

const CoachTaskAdminDetail = () => {
  const { params: { coachId }, url } = useRouteMatch();
  const history = useHistory();
  const { showToast } = useToast();
  const { userId: loggedInUserId } = useContext(LoggedInUserContext);
  // The detail route is `${dashboard}/:coachId`; the dashboard is the parent segment.
  const dashboardPath = url.slice(0, url.lastIndexOf('/'));

  const {
    isReady,
    activeCoachIds,
    coachNameById,
    tasksByCoach,
  } = useContext(CoachTaskAdminContext);
  const coachName = coachNameById[coachId];

  const [taskToRemove, setTaskToRemove] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  // Multi-select status filter; defaults to open work (finished tasks hidden).
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER);

  // Redirect once loaded if this coach isn't in the visible set — e.g. a hand-typed URL
  // for a coach the user neither manages nor has revealed via "show all". (Server-side
  // Firestore rules remain the authoritative boundary.)
  useEffect(() => {
    if (isReady && !activeCoachIds.includes(coachId)) {
      history.replace(dashboardPath);
    }
  }, [isReady, activeCoachIds, coachId, dashboardPath, history]);

  // An empty filter shows everything; otherwise keep only the selected statuses.
  const rows = (tasksByCoach[coachId] || [])
    .map((task) => ({ task, status: displayStatusOf(task) }))
    .filter(({ status }) => statusFilter.length === 0 || statusFilter.includes(status))
    .map(({ task, status }) => ({
      id: task.id,
      taskTitle: task.taskTitle,
      taskType: task.taskType,
      priority: task.priority,
      status,
      deadline: task.deadline,
      isOverdue: task.isOverdue,
      taskRef: task,
    }));

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingTask(null);
  }, []);

  const openCreate = useCallback(() => {
    setEditingTask(null);
    setIsModalOpen(true);
  }, []);

  const openEdit = useCallback((taskRef) => {
    setEditingTask(taskRef);
    setIsModalOpen(true);
  }, []);

  // The live task collections reflect the create/edit automatically — just toast.
  const handleSaved = useCallback((message) => {
    showToast(message);
  }, [showToast]);

  const handleError = useCallback((message = '') => {
    showToast(message || texts.taskError, { error: true });
  }, [showToast]);

  const handleRemove = useCallback(async () => {
    if (!taskToRemove) {
      return;
    }
    try {
      // Stamp who removed it — the remover can differ from the original assigner.
      if (taskToRemove.isAwaitingClientConsent && taskToRemove.highlightId) {
        // A draft takes its highlight with it, so the client's app stops asking about it.
        await ContentHighlight.withdrawDraft({
          taskId: taskToRemove.id, highlightId: taskToRemove.highlightId, removedBy: loggedInUserId,
        });
      } else {
        await taskToRemove.remove(loggedInUserId);
      }
    } catch (error) {
      Sentry.captureException(error, { extra: { coachId } });
      // Surface the failure — the task stays in the list, so don't leave the user thinking it worked.
      showToast(texts.taskError, { error: true });
    } finally {
      // The live task collections update the list automatically — no manual refresh.
      setTaskToRemove(null);
    }
  }, [taskToRemove, coachId, loggedInUserId, showToast]);

  const columns = [
    {
      field: 'taskTitle',
      headerName: texts.columns.title,
      flex: 2,
    },
    {
      field: 'taskType',
      headerName: texts.columns.type,
      flex: 1,
    },
    {
      field: 'priority',
      headerName: texts.columns.priority,
      flex: 1,
      renderCell: ({ value }) => (value ? <Chip label={value} size="small" variant="outlined" /> : null),
    },
    {
      field: 'status',
      headerName: texts.columns.status,
      flex: 1,
      // Show the same human-readable label the status filter uses, not the raw enum.
      valueFormatter: ({ value }) => texts.statusLabels[value] || value,
    },
    {
      field: 'deadline',
      headerName: texts.columns.deadline,
      flex: 1.5,
      valueGetter: ({ row }) => (row.deadline ? row.deadline.valueOf() : 0),
      renderCell: ({ row }) => (
        row.deadline
          ? (
            <OverdueText $hasOverdue={row.isOverdue}>
              {moment(row.deadline).format(DateFormat.DATE_MONTH_YEAR_TIME_12H_FORMAT)}
            </OverdueText>
          )
          : '—'
      ),
    },
    {
      field: 'actions',
      headerName: texts.columns.actions,
      sortable: false,
      filterable: false,
      flex: 1.4,
      /*
        A task the client declined is already removed: there is nothing left to edit or remove. One still
        waiting on them can only be removed, which withdraws its highlight — an edit could change what the
        client is being asked to agree to.
      */
      renderCell: ({ row }) => (row.status === TaskDisplayStatus.CLIENT_DECLINED ? null : (
        <ActionButtons>
          {row.status !== TaskDisplayStatus.AWAITING_CLIENT_CONSENT && (
            <Button
              size="small"
              variant="contained"
              onClick={() => openEdit(row.taskRef)}
            >
              {texts.edit}
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => setTaskToRemove(row.taskRef)}
          >
            {texts.remove}
          </Button>
        </ActionButtons>
      )),
    },
  ];

  return (
    <PageContainer>
      <HeaderRow>
        <TitleContainer>
          <BackButton label={texts.back} onClick={() => history.push(dashboardPath)} />
          <Title>{coachName || texts.title}</Title>
          <SubTitle>{texts.detailSubtitle}</SubTitle>
        </TitleContainer>
        <HeaderActions>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="task-status-filter-label">{texts.statusFilterLabel}</InputLabel>
            <Select
              labelId="task-status-filter-label"
              multiple
              value={statusFilter}
              onChange={(event) => {
                const { value } = event.target;
                setStatusFilter(typeof value === 'string' ? value.split(',') : value);
              }}
              input={<OutlinedInput label={texts.statusFilterLabel} />}
              renderValue={(selected) => selected.map((status) => texts.statusLabels[status] || status).join(', ')}
            >
              {STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox checked={statusFilter.includes(status)} />
                  <ListItemText primary={texts.statusLabels[status] || status} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={openCreate}>
            {texts.newTask}
          </Button>
        </HeaderActions>
      </HeaderRow>
      <GridWrapper>
        <GenericDataGrid
          rows={rows}
          columns={columns}
          loading={!isReady}
        />
      </GridWrapper>
      <CreateCoachTaskModal
        isOpen={isModalOpen}
        onDidDismiss={closeModal}
        onCreated={() => handleSaved(texts.taskCreated)}
        onUpdated={() => handleSaved(texts.taskUpdated)}
        onError={handleError}
        task={editingTask}
        coachId={coachId}
        coachName={coachName}
      />
      {taskToRemove && (
        <ConfirmationModal
          open={!!taskToRemove}
          onClose={() => setTaskToRemove(null)}
          onConfirm={handleRemove}
          title={texts.removeTitle}
          description={taskToRemove.taskTitle}
        />
      )}
    </PageContainer>
  );
};

export default CoachTaskAdminDetail;
